import { useCallback, useMemo, useRef, useState } from 'react';
import { DevicePreviewToggle, type PreviewDevice } from '@game-platform/theme-constructor';
import type { AnimationConfig, PreviewTemplate } from '@game-platform/pack-animation';
import { AnimationPreview, type AnimationPreviewHandle } from './AnimationPreview';
import type { PackArtwork } from './usePackArtwork';
import { PreviewCommands } from './PreviewCommands';
import { buildPreviewPayload, IFRAME_ORIGIN } from './previewPayload';

interface Props {
  animation: AnimationConfig;
  designSystem: Record<string, unknown>;
  template: PreviewTemplate;
  artwork: PackArtwork;
  /** A write is in flight: Reset to preset must not change what is being saved. */
  locked: boolean;
  /** Before the scene remounts: artwork links may have expired. */
  onReloadArtwork: () => void;
  onResetOptions: () => void;
}

/** The right side of the pack opening editor: the live scene with this pack's artwork. */
export function PackOpeningPreview({
  animation,
  designSystem,
  template,
  artwork,
  locked,
  onReloadArtwork,
  onResetOptions,
}: Props) {
  const [device, setDevice] = useState<PreviewDevice>('desktop');
  const [error, setError] = useState<string | null>(null);
  const previewRef = useRef<AnimationPreviewHandle>(null);
  const onError = useCallback((m: string) => setError(m), []);

  const payload = useMemo(
    () =>
      buildPreviewPayload({
        animation,
        designSystem,
        template,
        packImageUrl: artwork.packImageUrl,
        cardImageUrls: artwork.cardImageUrls,
      }),
    [animation, designSystem, template, artwork],
  );

  return (
    <section aria-label="Preview" className="flex min-w-0 flex-1 flex-col bg-neutral-950">
      <div className="flex shrink-0 flex-wrap items-center gap-2 border-b border-neutral-800 bg-neutral-900 px-3 py-2">
        <fieldset disabled={locked} className="contents">
          <PreviewCommands
            variant={animation.variant}
            hasOptions={Boolean(animation.options)}
            onCommand={(command) => previewRef.current?.send(command)}
            onResetPreview={() => {
              setError(null);
              previewRef.current?.send('reset');
              onReloadArtwork();
            }}
            onResetOptions={onResetOptions}
          />
        </fieldset>
        <div className="ml-auto">
          <DevicePreviewToggle value={device} onChange={setDevice} />
        </div>
      </div>
      <div
        className={
          'relative flex min-h-0 flex-1 overflow-hidden ' +
          (device === 'mobile' ? 'justify-center px-4 py-4' : '')
        }
      >
        {payload ? (
          <div
            className={
              device === 'mobile'
                ? 'h-full w-[375px] overflow-hidden rounded-lg border border-neutral-800 bg-neutral-900 shadow-2xl'
                : 'h-full w-full'
            }
          >
            <AnimationPreview
              ref={previewRef}
              iframeOrigin={IFRAME_ORIGIN}
              payload={payload}
              onError={onError}
            />
          </div>
        ) : (
          <div className="flex h-full w-full items-center justify-center px-8 text-center text-[13px] text-neutral-400">
            This pack has no pack image yet. Upload one under Images to preview its opening.
          </div>
        )}
        {error && (
          <div
            role="alert"
            className="absolute inset-x-4 bottom-4 rounded-md bg-black/80 px-3 py-2 font-mono text-[11px] text-red-300"
          >
            {error}
          </div>
        )}
      </div>
    </section>
  );
}
